import { Button, Grid, Popconfirm, Space, Tag, Typography } from "antd";
import { CheckCircleOutlined, DisconnectOutlined, EditOutlined, LinkOutlined, StopOutlined } from "@ant-design/icons";
import { useTranslation } from "react-i18next";
import type { components } from "@qorakul/shared/api";

type UserSummary = components["schemas"]["UserSummary"];

/** What the teacher and staff tables need from a row. */
export interface ProfileRow {
  id: string;
  fullName: string;
  isActive: boolean;
  user: UserSummary | null;
}

export function StatusTag({ active }: { active: boolean }) {
  const { t } = useTranslation();
  return active ? <Tag color="success">{t("person.status.active")}</Tag> : <Tag>{t("person.status.inactive")}</Tag>;
}

export function AccountCell({ user }: { user: UserSummary | null }) {
  const { t } = useTranslation();
  if (!user) return <Typography.Text type="secondary">{t("person.noAccount")}</Typography.Text>;
  return (
    <div style={{ lineHeight: 1.3 }}>
      <div>{user.displayName}</div>
      <Typography.Text type="secondary" style={{ fontSize: 12 }}>
        {user.userName}
      </Typography.Text>
    </div>
  );
}

interface ActionsProps {
  row: ProfileRow;
  /** Translated confirmation texts for this kind of profile. */
  deactivateConfirm: string;
  reactivateConfirm: string;
  canLink: boolean;
  busy: boolean;
  onEdit(): void;
  onToggle(): void;
  onLink(): void;
  onUnlink(): void;
}

/** Edit, activate/deactivate and link/unlink buttons; icon-only on phones, each with an accessible name. */
export function ProfileActions({ row, deactivateConfirm, reactivateConfirm, canLink, busy, onEdit, onToggle, onLink, onUnlink }: ActionsProps) {
  const { t } = useTranslation();
  const screens = Grid.useBreakpoint();
  const label = (text: string) => `${text}: ${row.fullName}`;
  const toggleText = row.isActive ? t("person.deactivate") : t("person.reactivate");

  return (
    <Space size={4} wrap>
      <Button size="small" icon={<EditOutlined />} onClick={onEdit} aria-label={label(t("common.edit"))}>
        {screens.lg && t("common.edit")}
      </Button>
      <Popconfirm
        title={toggleText}
        description={<div style={{ maxWidth: 320 }}>{row.isActive ? deactivateConfirm : reactivateConfirm}</div>}
        okText={t("person.confirm")}
        cancelText={t("person.cancel")}
        okButtonProps={{ danger: row.isActive }}
        onConfirm={onToggle}
      >
        <Button size="small" danger={row.isActive} icon={row.isActive ? <StopOutlined /> : <CheckCircleOutlined />} loading={busy} aria-label={label(toggleText)}>
          {screens.lg && toggleText}
        </Button>
      </Popconfirm>
      {canLink &&
        (row.user ? (
          <Popconfirm
            title={t("person.unlinkUser")}
            description={<div style={{ maxWidth: 320 }}>{t("person.unlinkConfirm", { name: row.fullName })}</div>}
            okText={t("person.confirm")}
            cancelText={t("person.cancel")}
            onConfirm={onUnlink}
          >
            <Button size="small" icon={<DisconnectOutlined />} aria-label={label(t("person.unlinkUser"))}>
              {screens.lg && t("person.unlinkUser")}
            </Button>
          </Popconfirm>
        ) : (
          <Button size="small" icon={<LinkOutlined />} onClick={onLink} aria-label={label(t("person.linkUser"))}>
            {screens.lg && t("person.linkUser")}
          </Button>
        ))}
    </Space>
  );
}
